import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Wallet, Plus, Filter, X, Upload, AlertCircle, DollarSign, TrendingDown, Users, CheckCircle, Lock, Eye, ArrowLeft, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale/es';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ESTADO_META = {
  disponible: { label: 'Disponible', tone: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-900/60' },
  parcial: { label: 'Parcialmente aplicado', tone: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 border-amber-200 dark:border-amber-900/60' },
  liquidado: { label: 'Liquidado', tone: 'bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300 border-slate-200 dark:border-slate-700' },
  cerrado: { label: 'Cerrado', tone: 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300 border-blue-200 dark:border-blue-900/60' },
};

const hasPerm = (user, perm) => (user?.role === 'admin') || (user?.permissions || []).includes(perm);

export default function Adelantos() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [adelantos, setAdelantos] = useState([]);
  const [totales, setTotales] = useState({ count: 0, monto_entregado: 0, monto_aplicado: 0, monto_disponible: 0, alertas_devolucion: 0 });
  const [users, setUsers] = useState([]);
  const [bancos, setBancos] = useState([]);

  // Filtros
  const [fBeneficiario, setFBeneficiario] = useState('');
  const [fFechaDesde, setFFechaDesde] = useState('');
  const [fFechaHasta, setFFechaHasta] = useState('');
  const [fEstado, setFEstado] = useState('');
  const [fBanco, setFBanco] = useState('');
  const [fSoloConSaldo, setFSoloConSaldo] = useState(false);

  // Modal nuevo adelanto
  const [showNew, setShowNew] = useState(false);
  const [newBeneficiario, setNewBeneficiario] = useState('');
  const [newMonto, setNewMonto] = useState('');
  const [newBanco, setNewBanco] = useState('');
  const [newCuenta, setNewCuenta] = useState('');
  const [newTitular, setNewTitular] = useState('');
  const [newTipoCuenta, setNewTipoCuenta] = useState('monetaria');
  const [newFechaDeposito, setNewFechaDeposito] = useState(new Date().toISOString().slice(0, 10));
  const [newNota, setNewNota] = useState('');
  const [newComprobante, setNewComprobante] = useState('');
  const [newBoletaFile, setNewBoletaFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const canCrear = hasPerm(user, 'crear_adelanto');
  const canCerrar = hasPerm(user, 'cerrar_adelanto');
  const canCreate = hasPerm(user, 'crear_adelanto');
  const canElegir = hasPerm(user, 'elegir_beneficiario_adelanto');

  useEffect(() => {
    if (canElegir) {
      axios.get(`${API}/adelantos/beneficiarios`).then(r => setUsers(r.data || [])).catch(() => {});
    }
    axios.get(`${API}/bancos`).then(r => setBancos(r.data || [])).catch(() => {});
  }, [canElegir]);

  const fetchAdelantos = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fBeneficiario) params.set('beneficiario_id', fBeneficiario);
      if (fFechaDesde) params.set('fecha_inicio', fFechaDesde);
      if (fFechaHasta) params.set('fecha_fin', fFechaHasta);
      if (fEstado) params.set('estado', fEstado);
      if (fBanco) params.set('banco', fBanco);
      if (fSoloConSaldo) params.set('con_saldo', 'true');
      const res = await axios.get(`${API}/adelantos?${params.toString()}`);
      setAdelantos(res.data.adelantos || []);
      setTotales(res.data.totales || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cargar adelantos');
    } finally {
      setLoading(false);
    }
  }, [fBeneficiario, fFechaDesde, fFechaHasta, fEstado, fBanco, fSoloConSaldo]);

  useEffect(() => {
    const t = setTimeout(fetchAdelantos, 300);
    return () => clearTimeout(t);
  }, [fetchAdelantos]);

  const resetNewForm = () => {
    setNewBeneficiario(''); setNewMonto(''); setNewBanco(''); setNewCuenta('');
    setNewTitular(''); setNewTipoCuenta('monetaria');
    setNewFechaDeposito(new Date().toISOString().slice(0, 10));
    setNewNota(''); setNewBoletaFile(null); setNewComprobante('');
  };

  const handleCreate = async () => {
    if (!newBeneficiario) return toast.error('Selecciona un beneficiario');
    const monto = parseFloat(newMonto);
    if (!monto || monto <= 0) return toast.error('Monto inválido');
    if (!newBanco.trim()) return toast.error('Ingresa el banco');
    setSaving(true);
    try {
      const res = await axios.post(`${API}/adelantos`, {
        beneficiario_id: newBeneficiario,
        monto_total: monto,
        banco: newBanco,
        numero_cuenta: newCuenta,
        titular_cuenta: newTitular,
        tipo_cuenta: newTipoCuenta,
        fecha_deposito: newFechaDeposito,
        nota: newNota,
        numero_comprobante: newComprobante,
      });
      const adelantoId = res.data?.id;
      // Subir boleta si existe
      if (newBoletaFile && adelantoId) {
        const fd = new FormData();
        fd.append('file', newBoletaFile);
        await axios.post(`${API}/adelantos/${adelantoId}/boleta`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success('Adelanto creado');
      setShowNew(false);
      resetNewForm();
      fetchAdelantos();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo crear el adelanto');
    } finally {
      setSaving(false);
    }
  };

  const clearFilters = () => {
    setFBeneficiario(''); setFFechaDesde(''); setFFechaHasta('');
    setFEstado(''); setFBanco(''); setFSoloConSaldo(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="adelantos-page">
      <div className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="container mx-auto px-4 pt-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="-ml-2 text-slate-600 dark:text-slate-300" data-testid="back-to-dashboard-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Volver al Dashboard
          </Button>
        </div>
        <div className="container mx-auto px-4 pb-4 pt-1 flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-2xl font-display font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Wallet className="h-6 w-6 text-[#2c57c5]" /> Adelantos (Bolsón)
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Depósitos entregados antes del gasto. Se aplican a pagos cuando el beneficiario los solicita.
            </p>
          </div>
          {canCrear && (
            <Button onClick={() => setShowNew(true)} className="bg-[#2c57c5] hover:bg-[#1e3a8a] text-white" data-testid="new-adelanto-btn">
              <Plus className="h-4 w-4 mr-2" /> Nuevo adelanto
            </Button>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* KPI cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Entregado" value={`Q${(totales.monto_entregado||0).toLocaleString('es-GT',{minimumFractionDigits:2})}`} icon={DollarSign} tone="blue" />
          <Kpi label="Aplicado a pagos" value={`Q${(totales.monto_aplicado||0).toLocaleString('es-GT',{minimumFractionDigits:2})}`} icon={CheckCircle} tone="emerald" />
          <Kpi label="Saldo disponible" value={`Q${(totales.monto_disponible||0).toLocaleString('es-GT',{minimumFractionDigits:2})}`} icon={Wallet} tone="amber" />
          <Kpi label="Pend. devolución (>30d)" value={totales.alertas_devolucion || 0} icon={AlertCircle} tone="red" />
        </div>

        {/* Filtros */}
        <Card className="p-5 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" data-testid="adelantos-filters">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
              <Filter className="h-4 w-4 text-[#2c57c5]" /> Filtros
            </h2>
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-slate-500 dark:text-slate-400">
              <X className="h-3.5 w-3.5 mr-1" /> Limpiar
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Desde</Label>
              <Input type="date" value={fFechaDesde} onChange={(e) => setFFechaDesde(e.target.value)} data-testid="f-desde" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Hasta</Label>
              <Input type="date" value={fFechaHasta} onChange={(e) => setFFechaHasta(e.target.value)} data-testid="f-hasta" />
            </div>
            {canElegir && <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Beneficiario</Label>
              <Select value={fBeneficiario || 'all'} onValueChange={(v) => setFBeneficiario(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="f-beneficiario"><SelectValue placeholder="Todos los usuarios" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los usuarios</SelectItem>
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>}
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Banco</Label>
              <Select value={fBanco || 'all'} onValueChange={(v) => setFBanco(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="f-banco"><SelectValue placeholder="Todos los bancos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los bancos</SelectItem>
                  {bancos.map(b => <SelectItem key={b.id || b.nombre} value={b.nombre}>{b.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Estado</Label>
              <Select value={fEstado || 'all'} onValueChange={(v) => setFEstado(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="f-estado"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="disponible">Disponible</SelectItem>
                  <SelectItem value="parcial">Parcialmente aplicado</SelectItem>
                  <SelectItem value="liquidado">Liquidado</SelectItem>
                  <SelectItem value="cerrado">Cerrado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                <input type="checkbox" checked={fSoloConSaldo} onChange={(e) => setFSoloConSaldo(e.target.checked)} className="w-4 h-4" data-testid="f-con-saldo" />
                Solo con saldo disponible
              </label>
            </div>
          </div>
        </Card>

        {/* Tabla */}
        {loading ? (
          <Card className="p-10 text-center bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <p className="text-slate-500 dark:text-slate-400">Cargando...</p>
          </Card>
        ) : adelantos.length === 0 ? (
          <Card className="p-10 text-center bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800" data-testid="empty-state">
            <Wallet className="h-10 w-10 text-slate-300 dark:text-slate-700 mx-auto mb-3" />
            <p className="text-slate-500 dark:text-slate-400">No hay adelantos con esos filtros</p>
          </Card>
        ) : (
          <Card className="overflow-hidden bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800" data-testid="adelantos-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400">
                  <tr>
                    <th className="text-left font-semibold px-4 py-3 uppercase text-[10px] tracking-wider">Fecha</th>
                    <th className="text-left font-semibold px-4 py-3 uppercase text-[10px] tracking-wider">Beneficiario</th>
                    <th className="text-left font-semibold px-4 py-3 uppercase text-[10px] tracking-wider">Banco</th>
                    <th className="text-right font-semibold px-4 py-3 uppercase text-[10px] tracking-wider">Entregado</th>
                    <th className="text-right font-semibold px-4 py-3 uppercase text-[10px] tracking-wider">Aplicado</th>
                    <th className="text-right font-semibold px-4 py-3 uppercase text-[10px] tracking-wider">Saldo</th>
                    <th className="text-left font-semibold px-4 py-3 uppercase text-[10px] tracking-wider">Estado</th>
                    <th className="text-left font-semibold px-4 py-3 uppercase text-[10px] tracking-wider">Acción</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {adelantos.map(a => {
                    const meta = ESTADO_META[a.estado] || ESTADO_META.disponible;
                    let fecha = '';
                    try { fecha = format(new Date(a.created_at), 'dd MMM yy', { locale: es }); } catch { /* ignore */ }
                    return (
                      <tr key={a.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors" data-testid={`adelanto-row-${a.id}`}>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-600 dark:text-slate-400">{fecha}</td>
                        <td className="px-4 py-3 text-slate-900 dark:text-slate-100 font-medium">{a.beneficiario_nombre}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{a.banco || '—'}</td>
                        <td className="px-4 py-3 text-right text-slate-900 dark:text-slate-100 tabular-nums">Q{Number(a.monto_total).toLocaleString('es-GT', { minimumFractionDigits: 2 })}</td>
                        <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-400 tabular-nums">Q{Number(a.monto_aplicado).toLocaleString('es-GT', { minimumFractionDigits: 2 })}</td>
                        <td className="px-4 py-3 text-right font-semibold text-emerald-700 dark:text-emerald-300 tabular-nums">Q{Number(a.monto_disponible).toLocaleString('es-GT', { minimumFractionDigits: 2 })}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={meta.tone}>{meta.label}</Badge>
                          {a.pendiente_devolucion && (
                            <Badge variant="outline" className="ml-1 bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900/60 text-[10px]">
                              <AlertCircle className="h-3 w-3 mr-0.5 inline" />>30 días
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/adelantos/${a.id}`)}
                            data-testid={`view-${a.id}`}
                          >
                            <Eye className="h-3.5 w-3.5 mr-1" /> Ver
                          </Button>
                          {canCreate && a.estado !== 'cerrado' && a.estado !== 'liquidado' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                if (!window.confirm(`¿Enviar el recordatorio por correo a ${a.beneficiario_nombre}?`)) return;
                                try {
                                  const r = await axios.post(`${API}/adelantos/${a.id}/send-reminder`);
                                  toast.success(`Recordatorio enviado a ${r.data.to}`);
                                  fetchAdelantos();
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'No se pudo enviar');
                                }
                              }}
                              className="text-slate-500 hover:text-[#2c57c5]"
                              data-testid={`remind-${a.id}`}
                              title={a.recordatorio_estado === 'enviado' ? `Recordatorio ya enviado (${a.recordatorio_detalle}). Reenviar` : 'Enviar recordatorio por correo'}
                            >
                              <Mail className={`h-3.5 w-3.5 ${a.recordatorio_estado === 'enviado' ? 'text-emerald-600' : ''}`} />
                            </Button>
                          )}
                          {canCerrar && a.estado !== 'cerrado' && a.estado !== 'liquidado' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                if (!window.confirm('¿Cerrar este adelanto? El saldo restante quedará como no utilizado.')) return;
                                try {
                                  await axios.post(`${API}/adelantos/${a.id}/close`, { motivo: '' });
                                  toast.success('Adelanto cerrado');
                                  fetchAdelantos();
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'No se pudo cerrar');
                                }
                              }}
                              className="text-slate-500 hover:text-blue-600"
                              data-testid={`close-${a.id}`}
                              title="Cerrar manualmente"
                            >
                              <Lock className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Modal nuevo adelanto */}
      <Dialog open={showNew} onOpenChange={(v) => { if (!v) { setShowNew(false); resetNewForm(); } }}>
        <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-[#2c57c5]" /> Nuevo adelanto
            </DialogTitle>
            <DialogDescription>
              Registra un depósito entregado por adelantado. Luego podrás aplicarlo a pagos del mismo beneficiario.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Beneficiario <span className="text-red-500">*</span></Label>
              {canElegir ? (
                <Select value={newBeneficiario} onValueChange={setNewBeneficiario}>
                  <SelectTrigger data-testid="new-beneficiario"><SelectValue placeholder="Elige un usuario" /></SelectTrigger>
                  <SelectContent>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-1" data-testid="no-perm-beneficiario">
                  No tienes el permiso "Elegir Beneficiarios de Adelantos". Pide al administrador que te lo asigne.
                </p>
              )}
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">El saldo solo podrá aplicarse a pagos creados por este usuario.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Monto (Q) <span className="text-red-500">*</span></Label>
                <Input type="number" step="0.01" min="0" value={newMonto} onChange={(e) => setNewMonto(e.target.value)} data-testid="new-monto" />
              </div>
              <div>
                <Label>Fecha depósito</Label>
                <Input type="date" value={newFechaDeposito} onChange={(e) => setNewFechaDeposito(e.target.value)} data-testid="new-fecha" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Banco <span className="text-red-500">*</span></Label>
                <Select value={newBanco} onValueChange={setNewBanco}>
                  <SelectTrigger data-testid="new-banco"><SelectValue placeholder="Selecciona banco" /></SelectTrigger>
                  <SelectContent>
                    {bancos.map(b => <SelectItem key={b.id || b.nombre} value={b.nombre}>{b.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tipo cuenta</Label>
                <Select value={newTipoCuenta} onValueChange={setNewTipoCuenta}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monetaria">Monetaria</SelectItem>
                    <SelectItem value="ahorro">Ahorro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Número de cuenta</Label>
                <Input value={newCuenta} onChange={(e) => setNewCuenta(e.target.value)} data-testid="new-cuenta" />
              </div>
              <div>
                <Label>Titular</Label>
                <Input value={newTitular} onChange={(e) => setNewTitular(e.target.value)} data-testid="new-titular" />
              </div>
            </div>
            <div>
              <Label>Número de comprobante</Label>
              <Input value={newComprobante} onChange={(e) => setNewComprobante(e.target.value)} placeholder="Ej: número de la boleta/transferencia" data-testid="new-comprobante" />
            </div>
            <div>
              <Label>Nota</Label>
              <Textarea rows={2} value={newNota} onChange={(e) => setNewNota(e.target.value)} placeholder="Ej: para gastos operativos del mes" data-testid="new-nota" />
            </div>
            <div>
              <Label>Boleta de transferencia (opcional)</Label>
              <Input type="file" accept="image/*,.pdf" onChange={(e) => setNewBoletaFile(e.target.files?.[0] || null)} data-testid="new-boleta" />
              {newBoletaFile && <p className="text-xs text-slate-500 mt-1">{newBoletaFile.name}</p>}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => { setShowNew(false); resetNewForm(); }} disabled={saving}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-[#2c57c5] hover:bg-[#1e3a8a] text-white" data-testid="save-adelanto">
              <Upload className="h-4 w-4 mr-2" /> {saving ? 'Guardando...' : 'Crear adelanto'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tone }) {
  const toneMap = {
    blue: 'from-blue-100 to-blue-200/70 dark:from-blue-950/60 dark:to-blue-900/40 text-[#2c57c5] dark:text-blue-300',
    emerald: 'from-emerald-100 to-emerald-200/70 dark:from-emerald-950/60 dark:to-emerald-900/40 text-emerald-600 dark:text-emerald-300',
    amber: 'from-amber-100 to-amber-200/70 dark:from-amber-950/60 dark:to-amber-900/40 text-amber-600 dark:text-amber-300',
    red: 'from-red-100 to-red-200/70 dark:from-red-950/60 dark:to-red-900/40 text-red-600 dark:text-red-300',
  };
  const t = toneMap[tone] || toneMap.blue;
  return (
    <Card className="p-5 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1">{label}</p>
          <p className="text-xl font-bold text-slate-900 dark:text-slate-100 tabular-nums truncate">{value}</p>
        </div>
        <div className={`bg-gradient-to-br ${t} rounded-xl p-3 shrink-0`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}
